import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import Field from '../Field';
import styles from './DateInput.module.scss';
const DateInput = ({ required = false, name, label, hint, error, onChange, value, defaultValue, className, order, shouldDisableValidation, shouldShowValidationIcon, disableLabel, disabled, shouldRemoveBorders = false, onBlur, shouldEnableInputAutoFocus = false, useFCSolution = false, }) => {
    const dayRef = useRef(null);
    const monthRef = useRef(null);
    const yearRef = useRef(null);
    const [dayValue, setDayValue] = useState('');
    const [monthValue, setMonthValue] = useState('');
    const [yearValue, setYearValue] = useState('');
    const changeInputValues = (isoDate) => {
        const [year, month, day] = isoDate.split('-');
        setYearValue(year);
        setMonthValue(month);
        setDayValue(day);
    };
    useEffect(() => {
        if (!value) {
            return;
        }
        changeInputValues(value);
    }, [value]);
    useEffect(() => {
        if (!defaultValue) {
            return;
        }
        changeInputValues(defaultValue);
    }, [defaultValue]);
    useEffect(() => {
        if (onChange) {
            const date = !yearValue || !monthValue || !dayValue ? '' : `${yearValue}-${monthValue}-${dayValue}`;
            onChange(date);
        }
    }, [dayValue, monthValue, yearValue]);
    const onInputBlur = (event) => {
        if (!yearValue || !monthValue || !dayValue) {
            return;
        }
        if (onBlur) {
            onBlur(event);
        }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear().toString();
        const formattedInputtedYear = yearValue.slice(-2);
        const formattedCurrentYear = currentYear.slice(-2);
        const yearPaddingFormat = parseInt(formattedCurrentYear, 10) > parseInt(formattedInputtedYear, 10) ? '200' : '190';
        const formattedYear = yearValue.padStart(4, yearPaddingFormat);
        const formattedMonth = monthValue.padStart(2, '0');
        const formattedDay = dayValue.padStart(2, '0');
        const date = `${formattedYear}-${formattedMonth}-${formattedDay}`;
        changeInputValues(date);
    };
    const handleFocus = () => {
        const firstInputType = order[0].datePart;
        if (firstInputType === 'day' && dayRef.current) {
            dayRef.current.focus();
        }
        else if (firstInputType === 'month' && monthRef.current) {
            monthRef.current.focus();
        }
        else if (firstInputType === 'year' && yearRef.current) {
            yearRef.current.focus();
        }
    };
    const FIELD_TYPE_MAP = {
        day: {
            setter: setDayValue,
            max: 2,
            ref: dayRef,
        },
        month: {
            setter: setMonthValue,
            max: 2,
            ref: monthRef,
        },
        year: {
            setter: setYearValue,
            max: 4,
            ref: yearRef,
        },
    };
    const handleInputChange = (type, index) => (event) => {
        var _a, _b, _c;
        const fieldValue = event.target.value;
        if (shouldEnableInputAutoFocus) {
            const allInputsHaveValues = !!dayValue && !!monthValue && !!yearValue;
            const nextInputIndex = index + 1;
            const shouldFocusNextInput = fieldValue.length === FIELD_TYPE_MAP[type].max &&
                nextInputIndex < order.length &&
                !allInputsHaveValues;
            const currentRef = (_b = FIELD_TYPE_MAP === null || FIELD_TYPE_MAP === void 0 ? void 0 : FIELD_TYPE_MAP[(_a = order === null || order === void 0 ? void 0 : order[nextInputIndex]) === null || _a === void 0 ? void 0 : _a.datePart]) === null || _b === void 0 ? void 0 : _b.ref;
            if (shouldFocusNextInput && !!currentRef) {
                (_c = currentRef.current) === null || _c === void 0 ? void 0 : _c.focus();
            }
            else if (!(order === null || order === void 0 ? void 0 : order[nextInputIndex]) && fieldValue.length === FIELD_TYPE_MAP[type].max) {
                event.target.blur();
            }
        }
        if (fieldValue.length <= FIELD_TYPE_MAP[type].max) {
            FIELD_TYPE_MAP[type].setter(fieldValue);
        }
    };
    const renderFields = () => {
        const result = [];
        order.map(({ datePart, placeholder, separator }, index) => {
            const baseProps = Object.assign({ 'aria-label': placeholder, 'aria-required': required, required, name: datePart, key: datePart, type: 'number', pattern: '\\d*', className: styles.input, placeholder, onBlur: onInputBlur, disabled }, (index === 0 && { id: name }));
            const dateProps = {
                day: Object.assign(Object.assign({}, baseProps), { maxLength: FIELD_TYPE_MAP.day.max, ref: dayRef, value: dayValue, onChange: handleInputChange('day', index) }),
                month: Object.assign(Object.assign({}, baseProps), { maxLength: FIELD_TYPE_MAP.month.max, ref: monthRef, value: monthValue, onChange: handleInputChange('month', index) }),
                year: Object.assign(Object.assign({}, baseProps), { maxLength: FIELD_TYPE_MAP.year.max, ref: yearRef, value: yearValue, onChange: handleInputChange('year', index) }),
            };
            const currentProps = dateProps[datePart];
            result.push(React.createElement("input", Object.assign({}, currentProps)));
            if (separator) {
                result.push(React.createElement("span", { key: `date-separator-${datePart}` }, separator));
            }
            return true;
        });
        return result;
    };
    return (React.createElement(Field, { required: required, name: name, hint: hint, label: label, error: error, className: className, handleDateFocus: handleFocus, shouldDisableValidation: shouldDisableValidation, shouldShowValidationIcon: shouldShowValidationIcon && !useFCSolution, disableLabel: disableLabel, value: value, render: ({ validationStatus }) => (React.createElement("div", { "data-testid": "dateInput", className: cn(styles.wrapper, disabled && styles.disabled, validationStatus === 'valid' && styles.valid, validationStatus === 'invalid' && styles.invalid, shouldRemoveBorders && styles.noBorders, useFCSolution && styles.wrapperFC) }, renderFields())) }));
};
export default DateInput;
//# sourceMappingURL=DateInput.js.map